/**
 * Provider Profile Hook
 *
 * Resolves whether the signed-in user has a PROVIDER profile, and its id.
 *
 * Deliberately NOT `usePermissionContext().isProvider` — that is derived from
 * `activeProfile`, which prefers ENTITY/ORGANIZATION for this portal, so a user holding
 * both an org profile and a provider profile would read as "not a provider".
 *
 * Also deliberately not a CASL check: AbilityProvider prefers the ENTITY/ORGANIZATION
 * profile's caslRules and only falls back to other profiles when that list is empty, so
 * a rule granted on a PROVIDER profile can be silently discarded.
 */

import { useMemo } from 'react'
import { usePermissionContext } from '@/contexts/PermissionContext'

export function useProviderProfile() {
  const { profiles, isLoadingPermissions } = usePermissionContext()

  const providerProfile = useMemo(
    () => profiles.find((p) => p.profile_type?.toUpperCase() === 'PROVIDER') ?? null,
    [profiles]
  )

  return {
    isProvider: !!providerProfile,
    providerProfile,
    isLoading: isLoadingPermissions,
  }
}
